import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { pathApi } from '../../api/services';
import { Skeleton } from '../../components/Feedback/Skeleton';
import { PageTransition } from '../../components/Transitions/PageTransition';
import type { PathDiagnosis, PathProfile, PathProfileResponse } from '../../types';
import { PathDiagnosisView } from './PathDiagnosisView';
import { PathSetupForm } from './PathSetupForm';

type View = 'loading' | 'error' | 'setup' | 'diagnosis';

export function PathPage() {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('loading');
  const [profile, setProfile] = useState<PathProfileResponse | null>(null);
  const [diagnosis, setDiagnosis] = useState<PathDiagnosis | null>(null);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setView('loading');
    try {
      const p = await pathApi.getProfile();
      setProfile(p);
      if (!p.profile) {
        setView('setup');
        return;
      }
      setDiagnosis(await pathApi.current());
      setView('diagnosis');
    } catch (err) {
      console.error('Failed to load path:', err);
      setView('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [view]);

  const save = async (data: PathProfile) => {
    setSaving(true);
    try {
      const saved = await pathApi.saveProfile(data);
      setProfile(saved);
      setDiagnosis(await pathApi.current());
      setView('diagnosis');
    } catch (err) {
      console.error('Failed to save path profile:', err);
      toast.error(t('path.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const recalculate = async () => {
    setRecalculating(true);
    try {
      setDiagnosis(await pathApi.recalculate());
      toast.success(t('path.recalculated'));
    } catch (err) {
      console.error('Failed to recalculate path:', err);
      toast.error(t('path.saveError'));
    } finally {
      setRecalculating(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await pathApi.deleteProfile();
      setDiagnosis(null);
      setProfile({ profile: null, source: null, confirmed: false, suggested: null, updated_at: null });
      setView('setup');
      toast.success(t('path.deleted'));
    } catch (err) {
      console.error('Failed to delete path profile:', err);
      toast.error(t('path.saveError'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageTransition>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {view === 'loading' && (
          <div className="space-y-4" aria-busy="true" aria-live="polite">
            <span className="sr-only">{t('common.loading')}</span>
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-28 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        )}

        {view === 'error' && (
          <div className="rounded-xl border border-border bg-surface p-6 text-center">
            <p className="font-medium text-text-primary">{t('path.loadError')}</p>
            <p className="mt-1 text-sm text-text-secondary">{t('common.tryAgainHint')}</p>
            <button onClick={load} className="mt-4 rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-secondary">
              {t('common.retry')}
            </button>
          </div>
        )}

        {view === 'setup' && (
          <div className="space-y-6">
            <header className="space-y-2">
              <h1 className="text-2xl font-bold text-text-primary [text-wrap:balance]">
                {profile?.profile ? t('path.setup.editTitle') : t('path.setup.title')}
              </h1>
              <p className="max-w-[65ch] text-text-secondary [text-wrap:pretty]">{t('path.setup.body')}</p>
              {!profile?.profile && profile?.suggested && (
                <p className="max-w-[65ch] text-sm text-text-secondary">{t('path.setup.prefilled')}</p>
              )}
              <p className="text-xs text-text-muted">
                {t('path.setup.privacy')}{' '}
                <Link to="/privacy" className="underline underline-offset-2 hover:text-text-primary">{t('path.setup.privacyLink')}</Link>
              </p>
            </header>
            <PathSetupForm
              initial={profile?.profile ?? profile?.suggested ?? null}
              saving={saving}
              onSubmit={save}
              onCancel={profile?.profile ? () => setView('diagnosis') : undefined}
            />
          </div>
        )}

        {view === 'diagnosis' && diagnosis && (
          <PathDiagnosisView
            diagnosis={diagnosis}
            onEdit={() => setView('setup')}
            onRecalculate={recalculate}
            recalculating={recalculating}
            onDelete={remove}
            deleting={deleting}
          />
        )}
      </div>
    </PageTransition>
  );
}
